import React, { useState, useRef, useEffect } from "react";
import Box from "@mui/material/Box";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import IconButton from "@mui/material/IconButton";
import { Skeleton, Typography } from "@mui/material";
import { usePricesContext } from "./Product";
import { getURLColor, constructImageUrl, handleImageFallback } from "utilities/imageUrlUtils";
import { selectVendorProduct } from "features/productsSlice";
import { useSelector } from "react-redux";
import { LOADING_STATES } from "features/productsSlice";

const LoadingState = () => {
    return (
        <Box
            className="p-[12px]"
            sx={{
                width: "232px",
                display: "flex",
                justifyContent: "center"
            }}
        >
            <Skeleton
                className="rounded-soft"
                variant="rectangular"
                width={198}
                height={175}
            />
        </Box>
    );
};

const ImageComingSoonPlaceholder = ({ imagesAvailable, noShowImg }) => {
    return (
        <Box
            sx={{
                flexGrow: 1,
                height: "100%",
                bgcolor: !imagesAvailable ? "#F6F6F6" : noShowImg ? "#f5f5f7" : "#fff",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                color: "#d2d6da",
                borderRadius: 1,
                p: 1.5,
            }}
        >
            <Typography
                variant="h2"
                sx={{
                    color: "inherit",
                    marginBottom: 0,
                }}
            >
                DGI
            </Typography>
            <Typography
                variant="body2"
                sx={{
                    color: "inherit",
                }}
            >
                Image coming soon
            </Typography>
        </Box>
    );
};

export default function ProductImage({ dgiStyle, smExclusive, hasColors }) {
    const { selectedColor } = usePricesContext();
    const [imagesAvailable, setImagesAvailable] = useState(true);
    const [imgIndex, setImgIndex] = useState(0);
    const [maxImgIndex, setMaxImgIndex] = useState(9);
    const [imgColor, setImgColor] = useState("");
    const arrowRef = useRef(null);
    const leftArrowRef = useRef(null);
    const rightArrowRef = useRef(null);
    const [tryHotlinking, setTryHotlinking] = useState(false);

    // More efficient selector that only checks vendors until it finds images
    const firstVendorWithImages = useSelector((state) => {
        const vendorPriorities = ["acc", "cm", "sta", "ab", "asc", "slc", "ss", "as"];

        for (const vendor of vendorPriorities) {
            const product = selectVendorProduct(state, dgiStyle, vendor, selectedColor?.name);
            const images = product?.data?.images || [];

            if (images.length > 0) {
                return { images, vendor };
            }
        }
        return null;
    });

    useEffect(() => {
        if (selectedColor) {
            const urlColor = getURLColor(selectedColor);
            setImgColor(urlColor);
            setImgIndex(0);
            setImagesAvailable(true);
            setTryHotlinking(false);
        }
    }, [selectedColor]);

    if (smExclusive) {
        return (
            <SanmarExclusiveProductImage
                dgiStyle={dgiStyle}
                masterColor={selectedColor?.name}
            />
        );
    }

    const primaryImgURL = constructImageUrl(dgiStyle, imgColor, imgIndex);

    const onArrowClick = (direction) => {
        if (direction === "left") {
            arrowRef.current = "left";
            if (imgIndex === 0) {
                setImgIndex(maxImgIndex);
            } else if (imgIndex > 0) {
                setImgIndex((prevImgIndex) => prevImgIndex - 1);
            }
        } else if (direction === "right") {
            arrowRef.current = "right";
            if (imgIndex >= maxImgIndex) {
                setImgIndex(0);
            } else if (imgIndex < maxImgIndex) {
                setImgIndex((prevImgIndex) => prevImgIndex + 1);
            }
        }
        return;
    };

    const onImgError = () => {
        if (imgIndex === 0) {
            // If our primary image fails, try hotlinking
            setTryHotlinking(true);
            if (!firstVendorWithImages) {
                setImagesAvailable(false);
            }
        }

        if (arrowRef.current === "left") {
            // No change for left arrow error
        } else if (arrowRef.current === "right") {
            setImgIndex(0);
            setMaxImgIndex(imgIndex - 1);
        }
    };

    const noShowImg = !imagesAvailable && !firstVendorWithImages;

    if (hasColors && !imgColor) {
        return <LoadingState />;
    }

    // If we're trying hotlinking and have hotlinked images available
    if (tryHotlinking && firstVendorWithImages) {
        return (
            <VendorHotlinkedProductImage
                images={firstVendorWithImages.images}
                vendor={firstVendorWithImages.vendor}
            />
        );
    }

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                bgcolor: "#fff",
                height: "auto",
                width: "232px",
                boxSizing: "border-box",
                borderRadius: 1,
                flexShrink: 0,
                position: "relative",
                p: noShowImg ? 3 : 0,
                pr: noShowImg ? 4 : 0,
                "&:hover .MuiIconButton-root": {
                    opacity: 1,
                },
                ".MuiIconButton-root": {
                    opacity: 0,
                    cursor: "pointer",
                    pointerEvents: "auto",
                },
            }}
        >
            {noShowImg ? (
                <ImageComingSoonPlaceholder
                    imagesAvailable={imagesAvailable}
                    noShowImg={noShowImg}
                />
            ) : (
                <>
                    <IconButton
                        id="left-carousel-arrow"
                        ref={leftArrowRef}
                        sx={{
                            opacity: 0,
                            cursor: "pointer",
                            pointerEvents: "auto",
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            onArrowClick("left");
                        }}
                    >
                        <ArrowBackIosNewIcon />
                    </IconButton>
                    <Box
                        sx={{
                            flexGrow: 1,
                            bgcolor: "#fff",
                            height: 190,
                            width: 152,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            color: "gray",
                            borderRadius: 1,
                            fontSize: "0.9em",
                            flexShrink: 0,
                            textAlign: "center",
                            position: "relative",
                        }}
                    >
                        <img
                            src={primaryImgURL}
                            onError={onImgError}
                            alt="Product image coming soon"
                            style={{
                                display: "block",
                                height: "auto",
                                maxHeight: "100%",
                                margin: 0,
                                maxWidth: "150px",
                                width: "auto",
                                zIndex: 2,
                            }}
                        />
                    </Box>
                    <IconButton
                        ref={rightArrowRef}
                        sx={{
                            opacity: 0,
                            cursor: "pointer",
                            pointerEvents: "auto",
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            onArrowClick("right");
                        }}
                    >
                        <ArrowForwardIosIcon />
                    </IconButton>
                </>
            )}
        </Box>
    );
}

const sharedStyles = {
    imageContainer: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        bgcolor: "#fff",
        height: "auto",
        width: "232px",
        boxSizing: "border-box",
        borderRadius: 1,
        flexShrink: 0,
        position: "relative",
        "&:hover .MuiIconButton-root": {
            opacity: 1,
        },
    },
    imageBox: {
        flexGrow: 1,
        bgcolor: "#fff",
        height: 190,
        width: 152,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        color: "gray",
        borderRadius: 1,
        fontSize: "0.9em",
        flexShrink: 0,
        textAlign: "center",
        position: "relative",
    },
    image: {
        display: "block",
        height: "auto",
        maxHeight: "100%",
        margin: 0,
        maxWidth: "150px",
        width: "auto",
        zIndex: 2,
    },
    navigationButton: {
        opacity: 0,
        cursor: "pointer",
        pointerEvents: "auto",
    },
};

function NoImageState({ message }) {
    return (
        <Box
            sx={{
                ...sharedStyles.imageContainer,
                p: 3,
                pr: 4,
            }}
        >
            <Box
                sx={{
                    flexGrow: 1,
                    height: "100%",
                    bgcolor: "#F6F6F6",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    color: "#d2d6da",
                    borderRadius: 1,
                    p: 1.5,
                }}
            >
                <Typography
                    variant="h2"
                    sx={{
                        color: "inherit",
                        marginBottom: 0,
                    }}
                >
                    DGI
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        color: "inherit",
                    }}
                >
                    {message}
                </Typography>
            </Box>
        </Box>
    );
}

function SanmarExclusiveProductImage({ dgiStyle, masterColor }) {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const leftArrowRef = useRef(null);
    const rightArrowRef = useRef(null);

    const product = useSelector((state) =>
        selectVendorProduct(state, dgiStyle, "sm", masterColor)
    );
    const images = product?.data?.images;

    const handlePrevious = (e) => {
        e.stopPropagation();
        setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    };

    const handleNext = (e) => {
        e.stopPropagation();
        setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    };

    // Loading state
    if (!product || product.loading === LOADING_STATES.LOADING_STATUS) {
        return <LoadingState />;
    }

    // No images state
    if (!images || images.length === 0) {
        return <NoImageState message="No images available" />;
    }

    return (
        <Box sx={sharedStyles.imageContainer}>
            <IconButton
                id="left-carousel-arrow"
                ref={leftArrowRef}
                sx={sharedStyles.navigationButton}
                onClick={handlePrevious}
            >
                <ArrowBackIosNewIcon />
            </IconButton>
            <Box sx={sharedStyles.imageBox}>
                <img
                    src={images[currentImageIndex]}
                    alt={`Product view ${currentImageIndex + 1}`}
                    style={sharedStyles.image}
                />
            </Box>
            <IconButton
                ref={rightArrowRef}
                sx={sharedStyles.navigationButton}
                onClick={handleNext}
            >
                <ArrowForwardIosIcon />
            </IconButton>
        </Box>
    );
}

// Add a new component for vendor hotlinked images
function VendorHotlinkedProductImage({ images, vendor }) {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const leftArrowRef = useRef(null);
    const rightArrowRef = useRef(null);

    const handlePrevious = (e) => {
        e.stopPropagation();
        setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    };

    const handleNext = (e) => {
        e.stopPropagation();
        setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    };

    // No images state
    if (!images || images.length === 0) {
        return <NoImageState message="No images available" />;
    }

    return (
        <Box sx={sharedStyles.imageContainer}>
            <IconButton
                id="left-carousel-arrow"
                ref={leftArrowRef}
                sx={sharedStyles.navigationButton}
                onClick={handlePrevious}
                disabled={images.length <= 1}
            >
                <ArrowBackIosNewIcon />
            </IconButton>
            <Box sx={sharedStyles.imageBox}>
                <img
                    src={images[currentImageIndex]}
                    alt={`${vendor.toUpperCase()} product view ${currentImageIndex + 1}`}
                    style={sharedStyles.image}
                />
            </Box>
            <IconButton
                ref={rightArrowRef}
                sx={sharedStyles.navigationButton}
                onClick={handleNext}
                disabled={images.length <= 1}
            >
                <ArrowForwardIosIcon />
            </IconButton>
        </Box>
    );
}
